import React, { useContext } from "react";
import { useQuery } from "@tanstack/react-query";
import { AppContext } from "../context/app.context";
import {
  MONGODB_AWSGW_QUERIES,
  MONGODB_AWSGW_APIKEY,
} from "../utility/keys";
import { buildEventLogForAttributeCellRequestBody } from "../utility/buildEventLogForAttributeCellRequestBody";
import { parseEventLogResponse } from "../utility/parseEventLogResponse";
import EventHistoryList from "./EventHistoryList";
import GridSpinner from "./GridSpinner";

/**
 * Time Machine #114 Phase 6 — History feed for ONE attribute cell. Used by
 * the new TimeMachineAttributeHistoryModal: when a user clicks an attribute
 * pseudo-job in the time-travel grid, we fetch every Attributes EventLog
 * entry whose before/after image matches BOTH `_kf_payrollNo` AND `date`.
 * Surfaces the full lifecycle (create / update / delete) of that specific
 * cell — answers helpdesk's "I set an attribute, now it's gone, who
 * changed it?"
 *
 * Props:
 *   payrollNo  — formatted payrollNo string (e.g. "3428.01")
 *   date       — date string in MM/DD/YYYY format (e.g. "11/6/2026")
 *   onScrubbed — optional fn() for double-click slider scrub +
 *                modal-close (per Phase 5 + 6 conventions)
 */
const AttributeHistorySection = ({ payrollNo, date, onScrubbed }) => {
  const { appEnvirons } = useContext(AppContext) || {};
  const mongoDatabase = appEnvirons?.mongoDb || "Warehouses";

  const isValidPayrollNo =
    typeof payrollNo === "string" && payrollNo.trim() !== "";
  const isValidDate =
    typeof date === "string" && /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(date);
  const enabled = isValidPayrollNo && isValidDate;

  const { data, isFetching, isError } = useQuery({
    queryKey: ["eventLogForAttributeCell", payrollNo, date],
    queryFn: async () => {
      const body = buildEventLogForAttributeCellRequestBody({
        payrollNo,
        date,
        database: mongoDatabase,
      });
      const response = await fetch(MONGODB_AWSGW_QUERIES, {
        method: "POST",
        mode: "cors",
        credentials: "omit",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "x-api-key": MONGODB_AWSGW_APIKEY,
        },
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        throw new Error(
          `AttributeHistorySection getEventLogForAttributeCell HTTP ${response.status}`
        );
      }
      return response.json();
    },
    select: (raw) => parseEventLogResponse(raw, "getEventLogForAttributeCell"),
    enabled,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    staleTime: Infinity,
  });

  return (
    <div
      data-testid="attribute-history-section"
      className="mt-4 relative"
      style={{ minHeight: "120px" }}
    >
      <h3 className="font-bold text-base mb-2">History</h3>
      <GridSpinner show={isFetching} label="Loading history…" />
      {!enabled ? (
        <div className="text-sm italic text-skin-muted">
          {!isValidPayrollNo
            ? "Missing payroll number."
            : "Missing or malformed date."}
        </div>
      ) : isError ? (
        <div className="text-sm italic" style={{ color: "#ff6b6b" }}>
          Couldn't load history. Please contact I.T.
        </div>
      ) : data && data.ok === false ? (
        <div className="text-sm italic" style={{ color: "#ff6b6b" }}>
          {data.reason || "Unknown error loading history."}
          {data.lambdaVersion ? ` · Lambda ${data.lambdaVersion}` : ""}
        </div>
      ) : (
        <EventHistoryList
          events={(data && data.ok && data.events) || []}
          showKindTag={false}
          onScrubbed={onScrubbed}
        />
      )}
    </div>
  );
};

export default AttributeHistorySection;
